import { useEffect, useRef, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { t } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import styles from './PictureViewer.module.css';

/** The picture a reader pressed: the address its bytes are drawn from, its alt text and its caption. */
export interface ViewedPicture {
  src: string;
  alt: string;
  caption: string;
}

/**
 * 🔒 A picture opened at full size over the page (T735; decision 1476): the
 * picture route's own bytes at their own size, in a window that scrolls, with
 * the caption beneath. 🔨 It is a viewer, not a form, so Escape, the × and a
 * press outside the picture each close it, and nothing typed is lost by any of
 * them. It is not the Modal shell, whose backdrop never cancels, and it holds
 * the focus on its one control while it is open.
 */
export function PictureViewer({ picture, onClose }: { picture: ViewedPicture; onClose: () => void }) {
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    close.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'Tab') {
        // The × is the window's one control, so the focus stays on it.
        event.preventDefault();
        close.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [onClose]);
  // A press on the backdrop or the empty room around the picture closes it; a press on the picture or its caption does not.
  const pressOutside = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };
  // Drawn at the document's end, so no scrolling tile or held toolbar of the page can clip or cover it.
  return createPortal(
    <div className={styles.backdrop} role="dialog" aria-modal="true" aria-label={picture.alt || t('kb.picture.viewerLabel')} data-kb-picture-viewer="true" onClick={pressOutside}>
      <button ref={close} type="button" className={styles.close} onClick={onClose} aria-label={t('kb.picture.close')} data-kb-picture-viewer-close="true">
        <Icon meaning="closeModal" size="inline" />
      </button>
      <div className={styles.room} onClick={pressOutside} data-kb-picture-viewer-room="true">
        <figure className={styles.figure}>
          <img className={styles.picture} src={picture.src} alt={picture.alt} />
          {picture.caption ? <figcaption className={styles.caption} data-kb-picture-viewer-caption="true">{picture.caption}</figcaption> : null}
        </figure>
      </div>
    </div>,
    document.body,
  );
}
